/**
 * IDEA CANVAS - Tab System
 * Tab switching and storyboard tab handlers
 *
 * Extracted from idea-canvas.js for better modularity
 * @version 1.1 - Removed PROMPT tab (use header copy dropdown instead)
 */

import { showError } from '../../core/error-handler.js';

// Storyboard imports
import {
  setActiveIdeaId,
  loadFromIdea,
  loadVideosFromDatabase,
  getStoryboard,
  hasPanelPool
} from '../../../features/generator/storyboard/state.js';

import {
  renderStoryboard,
  renderPanelsView,
  renderVideosTab,
  triggerSceneGeneration,
  importPanelsToScenes
} from '../../../features/generator/storyboard/ui.js';

// ============================================================================
// TAB SWITCHING
// ============================================================================

// Tab display names
const TAB_NAMES = {
  idea: 'IDEA',
  refine: 'REFINE IDEA',
  storyboard: 'STORYBOARD',
  scenes: 'SCENES',
  videos: 'VIDEO'
};

/**
 * Switch between tabs in the details section
 * @param {HTMLElement} canvasContainer - The canvas container element
 * @param {Object} currentIdea - The current idea object
 * @param {string} tabName - Name of the tab to switch to
 * @param {Object} callbacks - Callback functions for tab-specific actions
 */
export function switchTab(canvasContainer, currentIdea, tabName, callbacks = {}) {
  if (!canvasContainer) return;

  // Update tab card buttons (new card grid design)
  canvasContainer.querySelectorAll('.canvas-tab-card').forEach(t => {
    t.classList.toggle('active', t.dataset.tab === tabName);
  });

  // Update tab title headline
  const titleEl = canvasContainer.querySelector('#canvas-current-tab-title');
  if (titleEl && TAB_NAMES[tabName]) {
    titleEl.textContent = TAB_NAMES[tabName];
  }

  // Update tab content
  canvasContainer.querySelectorAll('.canvas-tab-content').forEach(c => {
    c.classList.toggle('active', c.dataset.tab === tabName);
  });

  // Trigger renders for specific tabs
  if (!currentIdea) return;
  const ideaId = String(currentIdea.id);

  switch (tabName) {
    case 'scenes':
      setActiveIdeaId(ideaId);
      renderStoryboard(ideaId, currentIdea);
      // Load videos from database (in case session is stale)
      loadVideosFromDatabase(ideaId);
      break;

    case 'videos':
      setActiveIdeaId(ideaId);
      // Load idea data (videos, scenes) if not already loaded
      loadFromIdea(currentIdea);
      // Render immediately with what we have
      renderVideosTab();
      // Then load videos from database (in case session is stale) and re-render
      loadVideosFromDatabase(ideaId).then(() => {
        renderVideosTab();  // Re-render with any new videos from database
      });
      break;

    case 'storyboard':
      callbacks.populateStoryboardPrompt?.(currentIdea);
      // Render panels view (now part of storyboard tab)
      setActiveIdeaId(ideaId);
      loadFromIdea(currentIdea);
      renderPanelsView();
      break;

    // NOTE: 'prompt' case removed - PROMPT tab is gone, use header copy dropdown

    case 'refine':
      callbacks.populateRefineTab?.(currentIdea);
      break;
  }
}

// ============================================================================
// STORYBOARD TAB HANDLERS
// ============================================================================

/**
 * Handle Generate Scenes button click
 * @param {Object} currentIdea - The current idea
 * @param {Function} setIdeaContext - Function to set idea context
 */
export async function handleGenerateScenes(currentIdea, setIdeaContext) {
  if (!currentIdea) return;
  setIdeaContext(currentIdea);
  // Use GeneratorState brief, or fall back to idea's own brief context
  const brief = window.GeneratorState?.brief || currentIdea.meta?.brief || {};
  await triggerSceneGeneration(currentIdea, brief);
}

/**
 * Handle Import Scenes button click
 * @param {Object} currentIdea - The current idea
 */
export function handleImportScenes(currentIdea) {
  if (!currentIdea) return;
  const ideaId = String(currentIdea.id);

  if (!hasPanelPool(ideaId)) {
    if (window.Toast) {
      window.Toast.warning('No panels available. Split a storyboard image first in the Storyboard tab.');
    } else {
      showError('No panels available. Split a storyboard image first in the Storyboard tab.', { context: 'importing scenes' });
    }
    return;
  }

  if (!getStoryboard(ideaId)?.scenes?.length) {
    if (window.Toast) {
      window.Toast.warning('No scenes available. Generate scenes first.');
    } else {
      showError('No scenes available. Generate scenes first.', { context: 'importing scenes' });
    }
    return;
  }

  // Import panels to scenes
  importPanelsToScenes(ideaId);
}

/**
 * Handle Generate Storyboard Image button click
 * Opens the image generation modal with the storyboard prompt
 * @param {Object} currentIdea - The current idea
 */
export async function handleGenerateStoryboardImage(currentIdea) {
  if (!currentIdea) return;
  const prompt = document.getElementById('canvas-storyboard-textarea')?.value;
  if (!prompt) return;

  // Get selected aspect ratio from storyboard settings
  const aspectSelect = document.getElementById('canvas-storyboard-aspect');
  const aspectRatio = aspectSelect?.value || '1:1';

  // Open image generation modal with storyboard prompt and aspect ratio
  const { openImageGenModal } = await import('../../../features/generator/features/image-generation.js');
  openImageGenModal(String(currentIdea.id), prompt, aspectRatio);
}

/**
 * Handle Copy Storyboard Prompt button click
 */
export function handleCopyStoryboardPrompt() {
  const textarea = document.getElementById('canvas-storyboard-textarea');
  if (textarea && textarea.value) {
    navigator.clipboard.writeText(textarea.value).then(() => {
      if (window.Toast) window.Toast.success('Copied to clipboard');
    });
  }
}

// NOTE: handleCopyFullPrompt removed - PROMPT tab is gone
// Use the copy dropdown in header instead (accessible via copy-dropdown.js)
